// src/components/marketing/Hero.tsx
'use client'

import { useEffect, useState, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { 
  ArrowRight, 
  Sparkles, 
  BookOpen,
  Shield,
  Zap,
  Target,
  Activity,
  GraduationCap,
  ChevronDown,
  BarChart3,
  Trophy,
  Users,
  Clock,
  CheckCircle2
} from 'lucide-react'

// Typewriter effect hook
function useTypewriter(words: string[], typingSpeed = 100, deletingSpeed = 50, pauseTime = 2000) {
  const [displayText, setDisplayText] = useState('')
  const [wordIndex, setWordIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const currentWord = words[wordIndex]
    
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1))
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime)
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentWord.slice(0, displayText.length - 1))
        } else {
          setIsDeleting(false)
          setWordIndex((prev) => (prev + 1) % words.length)
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed)

    return () => clearTimeout(timer)
  }, [displayText, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pauseTime])

  return displayText
}

// Floating particles component
function FloatingParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    class Particle {
      x: number
      y: number
      size: number
      speedX: number
      speedY: number
      opacity: number

      constructor() {
        if (!canvas) {
          this.x = 0
          this.y = 0
        } else {
          this.x = Math.random() * canvas.width
          this.y = Math.random() * canvas.height
        }
        this.size = Math.random() * 3 + 1
        this.speedX = Math.random() * 0.5 - 0.25
        this.speedY = Math.random() * 0.5 - 0.25
        this.opacity = Math.random() * 0.5 + 0.2
      }

      update() {
        if (!canvas) return
        this.x += this.speedX
        this.y += this.speedY

        if (this.x > canvas.width) this.x = 0
        if (this.x < 0) this.x = canvas.width
        if (this.y > canvas.height) this.y = 0
        if (this.y < 0) this.y = canvas.height
      }

      draw() {
        if (!ctx) return
        ctx.fillStyle = `rgba(99, 102, 241, ${this.opacity})`
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    const particles: Particle[] = []
    for (let i = 0; i < 35; i++) {
      particles.push(new Particle())
    }

    let animationId: number
    function animate() {
      if (!ctx || !canvas) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      particles.forEach(particle => {
        particle.update()
        particle.draw()
      })
      
      animationId = requestAnimationFrame(animate)
    }
    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.4 }}
    />
  )
}

const EXAM_OPTIONS = [
  { id: 'jee-main', label: 'JEE Main', icon: <Target className="w-4 h-4"/> },
  { id: 'jee-advanced', label: 'JEE Advanced', icon: <Zap className="w-4 h-4"/> },
  { id: 'neet-ug', label: 'NEET UG', icon: <Activity className="w-4 h-4"/> },
  { id: 'class-12', label: 'Class 12th', icon: <BookOpen className="w-4 h-4"/> },
  { id: 'class-11', label: 'Class 11th', icon: <GraduationCap className="w-4 h-4"/> },
]

// Educational Rotating Hero Slides (6-second clockwise rotation in one direction)
const HERO_SLIDES = [
  {
    id: 1,
    image: '/images/home/hero-aspirant-india-1.jpg',
    tag: 'Live Exam Simulation',
    caption: 'Indian aspirants practicing on real NTA mock interface',
    badgeTop: {
      category: 'Exam UI',
      text: 'Real 2026 NTA Pattern',
      icon: <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />,
      bg: 'bg-blue-100 dark:bg-blue-900/60'
    },
    badgeSide: {
      category: 'AIR Prediction',
      text: 'AIR 142 • 99.4%ile',
      icon: <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
      bg: 'bg-amber-100 dark:bg-amber-900/60'
    }
  },
  {
    id: 2,
    image: '/images/home/hero-aspirant-india-2.jpg',
    tag: 'Step-by-Step Problem Solving',
    caption: 'Targeted physics, chemistry & math question sets',
    badgeTop: {
      category: 'Practice Bank',
      text: '200,000+ Questions',
      icon: <BookOpen className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />,
      bg: 'bg-emerald-100 dark:bg-emerald-900/60'
    },
    badgeSide: {
      category: 'Score Growth',
      text: '+42 Marks Avg Boost',
      icon: <BarChart3 className="w-4 h-4 text-purple-600 dark:text-purple-400" />,
      bg: 'bg-purple-100 dark:bg-purple-900/60'
    }
  },
  {
    id: 3,
    image: '/images/home/hero-aspirant-india-3.jpg',
    tag: 'NCERT & PYQ Mastery',
    caption: 'Comprehensive chapter-wise mock tests and revision sets',
    badgeTop: {
      category: 'Aspirants',
      text: '5,000+ JEE/NEET Students',
      icon: <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />,
      bg: 'bg-purple-100 dark:bg-purple-900/60'
    },
    badgeSide: {
      category: 'Target Goal',
      text: 'Top IIT / AIIMS Rank',
      icon: <GraduationCap className="w-4 h-4 text-pink-600 dark:text-pink-400" />,
      bg: 'bg-pink-100 dark:bg-pink-900/60'
    }
  },
  {
    id: 4,
    image: '/images/home/hero-aspirant-india-4.jpg',
    tag: 'Exam Desk Preparation',
    caption: 'Formulas, rough sheet practice, and timed mock drills',
    badgeTop: {
      category: 'Time Control',
      text: 'Strict NTA Countdown',
      icon: <Clock className="w-4 h-4 text-rose-600 dark:text-rose-400" />,
      bg: 'bg-rose-100 dark:bg-rose-900/60'
    },
    badgeSide: {
      category: 'Instant Insights',
      text: 'Deep Weakness Analysis',
      icon: <Zap className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />,
      bg: 'bg-cyan-100 dark:bg-cyan-900/60'
    }
  }
]

export function Hero() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [isHovering, setIsHovering] = useState(false)
  const [selectedExam, setSelectedExam] = useState(EXAM_OPTIONS[0])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [isSlideHovered, setIsSlideHovered] = useState(false)
  const heroRef = useRef<HTMLDivElement>(null)

  const dynamicWord = useTypewriter(['JEE Main', 'NEET UG', 'JEE Advanced', 'Class 12th', 'Class 11th'], 80, 40, 2500)

  // Clockwise forward rotation every 6 seconds in one direction
  useEffect(() => {
    if (isSlideHovered) return
    const timer = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [isSlideHovered])

  // Mouse move effect for gradient follow
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect()
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        })
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const scrollToContent = () => {
    const element = document.getElementById('exams')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo({
        top: window.innerHeight - 80,
        behavior: 'smooth'
      })
    }
  }

  const currentSlide = HERO_SLIDES[currentSlideIndex]

  return (
    <section 
      ref={heroRef}
      className="relative min-h-[92vh] flex items-center justify-center overflow-hidden bg-white dark:bg-gray-950 pt-8 pb-16"
    >
      {/* Vibrant Animated Gradient Background */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-indigo-50/50 to-purple-50/80 dark:from-gray-900 dark:via-blue-950/60 dark:to-indigo-950/60"
        style={{
          backgroundPosition: `${mousePosition.x}% ${mousePosition.y}%`,
          transition: 'background-position 0.3s ease-out'
        }}
      >
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgb(99, 102, 241) 1px, transparent 1px),
              linear-gradient(to bottom, rgb(99, 102, 241) 1px, transparent 1px)
            `,
            backgroundSize: '4rem 4rem'
          }}
        />
      </div>

      <FloatingParticles />

      {/* Vibrant Floating Orbs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply dark:mix-blend-lighten filter blur-3xl opacity-25 animate-blob" />
      <div className="absolute top-40 right-10 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply dark:mix-blend-lighten filter blur-3xl opacity-25 animate-blob animation-delay-2000" />
      <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full mix-blend-multiply dark:mix-blend-lighten filter blur-3xl opacity-25 animate-blob animation-delay-4000" />

      {/* Main Content Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Text & CTAs */}
          <div className="lg:col-span-7 space-y-7 text-center lg:text-left">
            
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Strict Exam Environment
                </span>
              </div>
              <div className="w-px h-3.5 bg-gray-300 dark:bg-gray-600" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Latest 2026 NTA Pattern
                </span>
              </div>
            </div>

            {/* Main Headline with Typewriter */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-[1.12] tracking-tight">
                Ace Your{' '}
                <span className="relative inline-block min-w-[240px] sm:min-w-[280px] text-left">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient-x">
                    {dynamicWord}
                  </span>
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 w-0.5 sm:w-1 h-[0.9em] bg-blue-600 animate-blink" />
                </span>
                <br />
                Preparation
              </h1>
              
              <p className="max-w-2xl text-lg sm:text-xl text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
                Targeted mock tests, NCERT-verified questions, step-by-step solutions, and{' '}
                <span className="font-bold text-purple-600 dark:text-purple-400">instant All-India rank prediction.</span>
              </p>
            </div>

            {/* Interactive Intent Catcher (Selector) */}
            <div className="space-y-3">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Select your target exam
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2.5">
                {EXAM_OPTIONS.map((exam) => (
                  <button
                    key={exam.id}
                    onClick={() => setSelectedExam(exam)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all duration-300 text-xs sm:text-sm font-medium shadow-sm hover:shadow-md
                      ${selectedExam.id === exam.id 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 border-transparent text-white scale-105 shadow-purple-500/25' 
                        : 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                      }`}
                  >
                    {exam.icon}
                    {exam.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic CTA */}
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start items-center pt-2">
              {/* Primary CTA */}
              <Link href="/exams">
                <div
                  className="group relative px-7 py-4 text-base sm:text-lg font-semibold rounded-full overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white shadow-xl hover:shadow-purple-500/40 transition-all duration-300 hover:scale-105 cursor-pointer flex items-center justify-center w-full sm:w-fit"
                  onMouseEnter={() => setIsHovering(true)}
                  onMouseLeave={() => setIsHovering(false)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <span className="relative flex items-center gap-2.5">
                    <Sparkles className="w-5 h-5" />
                    Start {selectedExam.label} Mock Test
                    <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isHovering ? 'translate-x-1' : ''}`} />
                  </span>
                </div>
              </Link>

              {/* Secondary CTA */}
              <button
                onClick={scrollToContent}
                className="group px-7 py-4 text-base sm:text-lg font-semibold bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 text-gray-700 dark:text-gray-300 shadow-sm hover:shadow-md transition-all duration-300 rounded-full hover:scale-105 flex items-center gap-2.5 w-full sm:w-fit justify-center"
              >
                <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Browse Exams
              </button>
            </div>

            {/* Value Highlights */}
            <div className="pt-4 flex flex-wrap justify-center lg:justify-start gap-3 text-xs sm:text-sm">
              {[
                { icon: <Target className="w-4 h-4 text-blue-600" />, label: 'Chapter-wise Mocks' },
                { icon: <BarChart3 className="w-4 h-4 text-purple-600" />, label: 'All-India Rank Predictor' },
                { icon: <Zap className="w-4 h-4 text-amber-500" />, label: 'Step-by-Step Solutions' }
              ].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-gray-200 dark:border-gray-700 shadow-xs">
                  {stat.icon}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Clockwise Rotating Authentic Educational Photography */}
          <div 
            className="lg:col-span-5 relative flex flex-col justify-center items-center"
            onMouseEnter={() => setIsSlideHovered(true)}
            onMouseLeave={() => setIsSlideHovered(false)}
          >
            
            {/* Ambient Backlight */}
            <div className="absolute -inset-2 bg-gradient-to-tr from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl transform -rotate-1 pointer-events-none" />

            {/* Visual Frame */}
            <div className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border-4 border-white dark:border-gray-800 bg-gray-900 group">
              
              {/* Slides Container - Smooth single-direction forward crossfade */}
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                {HERO_SLIDES.map((slide, idx) => (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                      idx === currentSlideIndex
                        ? 'opacity-100 scale-100 z-10'
                        : 'opacity-0 scale-105 z-0 pointer-events-none'
                    }`}
                  >
                    <Image
                      src={slide.image}
                      alt={slide.caption}
                      fill
                      priority={idx === 0}
                      sizes="(max-width: 768px) 100vw, 40vw"
                      className="object-cover object-center"
                    />
                    
                    {/* Gradient Overlay for high readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-black/20" />
                  </div>
                ))}
                
                {/* Bottom photo dynamic caption */}
                <div className="absolute bottom-4 left-4 right-4 text-white z-20 transition-all duration-500">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400 mb-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    {currentSlide.tag}
                  </div>
                  <p className="text-sm font-bold text-white leading-snug">
                    {currentSlide.caption}
                  </p>
                </div>
              </div>

              {/* Floating Badge 1: Top-Left */}
              <div className="absolute top-4 -left-3 sm:-left-5 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-4 py-2.5 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 flex items-center gap-3 animate-float-slow z-30 transition-all duration-500">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${currentSlide.badgeTop.bg}`}>
                  {currentSlide.badgeTop.icon}
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {currentSlide.badgeTop.category}
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                    {currentSlide.badgeTop.text}
                  </div>
                </div>
              </div>

              {/* Floating Badge 2: Right */}
              <div className="absolute top-1/2 -right-3 sm:-right-6 -translate-y-1/2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md px-4 py-2.5 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 flex items-center gap-3 animate-float-delay z-30 transition-all duration-500">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${currentSlide.badgeSide.bg}`}>
                  {currentSlide.badgeSide.icon}
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {currentSlide.badgeSide.category}
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                    {currentSlide.badgeSide.text}
                  </div>
                </div>
              </div>

            </div>

            {/* Clockwise Progress Indicators (Forward rotation dots with smooth timer) */}
            <div className="flex items-center gap-2 mt-4 z-20">
              {HERO_SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlideIndex(idx)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    idx === currentSlideIndex
                      ? 'w-8 bg-gradient-to-r from-blue-600 to-purple-600 shadow-sm'
                      : 'w-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

          </div>

        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-950 to-transparent pointer-events-none" />

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -20px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(20px, 20px) scale(1.05); }
        }
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes float-delay {
          0%, 100% { transform: translateY(-50%) translateY(0px); }
          50% { transform: translateY(-50%) translateY(-8px); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-blink { animation: blink 1s step-end infinite; }
        .animate-float-slow { animation: float-slow 4s ease-in-out infinite; }
        .animate-float-delay { animation: float-delay 4.5s ease-in-out infinite 1s; }
      `}</style>
    </section>
  )
}